import { useI18nextInstance } from "@squide/i18next";
import { Fragment, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ComparisonDimension } from "#shared/comparisonDimension.ts";
import { type ComparisonBreakdown, type ComparisonInput, type ComparisonSelection, toComparisonInput } from "#shared/comparisonSelection.ts";
import { Button } from "@/components/ui/button.tsx";
import { Card, CardContent } from "@/components/ui/card.tsx";
import { useAnalyticsFilters } from "../hooks/useAnalyticsFilters.ts";
import { toAnalyticsBreakdownDimension, useAnalyticsTracking } from "../hooks/useAnalyticsTracking.ts";
import { useComparisonSelections } from "../hooks/useComparisonSelections.ts";
import { i18NextInstanceKey } from "../i18n/i18next.ts";
import { BASE_SERIES_ID } from "./comparison/comparisonSeries.ts";
import { BreakdownGroupRows } from "./comparison/BreakdownGroupRows.tsx";
import { ComparisonRowActions } from "./comparison/ComparisonRowActions.tsx";
import { ComparisonSeriesRow } from "./comparison/ComparisonSeriesRow.tsx";
import type { ComparisonSeries } from "./comparison/useComparisonSeries.ts";
import { FilterBar } from "./FilterBar.tsx";

interface CustomComparisonRowProps {
    node: ComparisonSelection;
    series: ComparisonSeries | undefined;
    maxBreakdownSelections: number;
    usedSeriesCount: number;
    maxSeriesCount: number;
    onUpdate: (id: string, selection: ComparisonInput) => void;
    onRemove: (id: string) => void;
    onBreakdownChange: (nodeId: string, dimension: ComparisonDimension, groupKeys: string[]) => void;
}

function CustomComparisonRow({ node, series, maxBreakdownSelections, usedSeriesCount, maxSeriesCount, onUpdate, onRemove, onBreakdownChange }: CustomComparisonRowProps) {
    const handleChange = useCallback((next: ComparisonInput) => onUpdate(node.id, next), [onUpdate, node.id]);

    const label = series?.label ?? "";
    const selection = useMemo(() => toComparisonInput(node), [node]);

    return (
        <ComparisonSeriesRow
            letter={series?.letter ?? ""}
            swatchClassName={series?.swatchClassName ?? ""}
            label={label}
            selection={selection}
            onChange={handleChange}
            trailingContent={<ComparisonRowActions nodeId={node.id} label={label} breakdown={node.breakdown} maxBreakdownSelections={maxBreakdownSelections} usedSeriesCount={usedSeriesCount} maxSeriesCount={maxSeriesCount} onBreakdownChange={onBreakdownChange} onRemove={onRemove} />}
        />
    );
}

export function AnalyticsFiltersCard() {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("AnalyticsFiltersCard", { i18n: i18nextInstance });

    const { comparisons, baseBreakdown, comparisonNodes, series, usedSeriesCount, maxSeriesCount, remainingSeriesSlots, duplicateComparison, updateComparison, removeComparison, clearComparisons, setBreakdown } = useComparisonSelections();
    const { clearFilters } = useAnalyticsFilters();
    const { trackBrokenDown, trackComparisonAdded, trackFiltersCleared } = useAnalyticsTracking();
    const [baseSeries] = series;

    const seriesById = useMemo(() => new Map(series.map(entry => [entry.id, entry])), [series]);

    const isCompared = comparisons.length > 0;

    const handleBreakdownChange = useCallback(
        (nodeId: string, dimension: ComparisonDimension, groupKeys: string[]) => {
            if (groupKeys.length > 0) {
                trackBrokenDown(toAnalyticsBreakdownDimension(dimension));
            }

            setBreakdown(nodeId, dimension, groupKeys);
        },
        [trackBrokenDown, setBreakdown]
    );

    const handleDuplicate = useCallback(
        (sourceId: string) => {
            if (usedSeriesCount < maxSeriesCount) {
                trackComparisonAdded(usedSeriesCount + 1);
            }

            duplicateComparison(sourceId);
        },
        [trackComparisonAdded, usedSeriesCount, maxSeriesCount, duplicateComparison]
    );

    const handleClearAll = useCallback(() => {
        trackFiltersCleared();
        clearFilters();
        clearComparisons();
    }, [trackFiltersCleared, clearFilters, clearComparisons]);

    const toMaxBreakdownSelections = (breakdown: ComparisonBreakdown | undefined) => (breakdown?.groupKeys.length ?? 0) + remainingSeriesSlots;

    return (
        <Card className="[--gutter:--spacing(4)]">
            <CardContent className="flex flex-col gap-2">
                <FilterBar
                    series={isCompared && baseSeries ? { letter: baseSeries.letter, swatchClassName: baseSeries.swatchClassName, name: t("baseSeries", { letter: baseSeries.letter, label: baseSeries.label }) } : undefined}
                    trailingContent={
                        <ComparisonRowActions
                            nodeId={BASE_SERIES_ID}
                            label={baseSeries?.label ?? ""}
                            breakdown={baseBreakdown}
                            maxBreakdownSelections={toMaxBreakdownSelections(baseBreakdown)}
                            usedSeriesCount={usedSeriesCount}
                            maxSeriesCount={maxSeriesCount}
                            onDuplicate={handleDuplicate}
                            onBreakdownChange={handleBreakdownChange}
                        />
                    }
                />

                {baseBreakdown && <BreakdownGroupRows nodeId={BASE_SERIES_ID} breakdown={baseBreakdown} seriesById={seriesById} maxSelections={toMaxBreakdownSelections(baseBreakdown)} usedSeriesCount={usedSeriesCount} maxSeriesCount={maxSeriesCount} onBreakdownChange={handleBreakdownChange} />}

                {comparisonNodes.map(node => (
                    <Fragment key={node.id}>
                        <CustomComparisonRow
                            node={node}
                            series={seriesById.get(node.id)}
                            maxBreakdownSelections={toMaxBreakdownSelections(node.breakdown)}
                            usedSeriesCount={usedSeriesCount}
                            maxSeriesCount={maxSeriesCount}
                            onUpdate={updateComparison}
                            onRemove={removeComparison}
                            onBreakdownChange={handleBreakdownChange}
                        />
                        {node.breakdown && <BreakdownGroupRows nodeId={node.id} breakdown={node.breakdown} seriesById={seriesById} maxSelections={toMaxBreakdownSelections(node.breakdown)} usedSeriesCount={usedSeriesCount} maxSeriesCount={maxSeriesCount} onBreakdownChange={handleBreakdownChange} />}
                    </Fragment>
                ))}

                {isCompared && (
                    <div className="flex flex-wrap items-center gap-2">
                        <p className="text-muted-fg text-xs">{t("seriesUsage", { count: usedSeriesCount, total: maxSeriesCount })}</p>
                        <Button intent="plain" size="xs" className="text-muted-fg hover:text-fg" onPress={handleClearAll}>
                            {t("clearAll")}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
